import { brand, radius } from '@alison/ui';
import Check from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import { keyframes } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { type ReactNode, useEffect, useRef, useState } from 'react';

const STEP_ADVANCE_MS = 6_000;
const COUNTDOWN_TICK_MS = 1_000;
const HEADER_SPINNER_SIZE = 26;
const ACTIVE_SPINNER_SIZE = 13;
const MARK_BOX_SIZE = 18;
const DONE_MARK_SIZE = 16;
const PENDING_DOT_SIZE = 7;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

type StepStatus = 'done' | 'active' | 'pending';

export interface RunProgress {
  label: string;
  totalSeconds: number;
  etaSeconds: number;
  updatedAt: string;
}

interface StepRunProgressProps {
  runningTitle: string;
  runningSteps: string[];
  // The service's own estimate; replaces the timed step list when present.
  progress?: RunProgress | null;
  onRunInBackground?: () => void;
  backgroundHref?: string;
}

interface StepMarkProps {
  status: StepStatus;
}

interface SpinnerProps {
  size: number;
  thickness: number;
  label?: string;
}

function Spinner({ size, thickness, label }: SpinnerProps): ReactNode {
  return (
    <Box
      {...(label === undefined ? {} : { role: 'progressbar', 'aria-label': label })}
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: '50%',
        border: `${thickness}px solid`,
        borderColor: 'divider',
        borderTopColor: brand.purple,
        animation: `${spin} 0.8s linear infinite`,
      }}
    />
  );
}

function StepMark({ status }: StepMarkProps): ReactNode {
  return (
    <Box
      sx={{
        width: MARK_BOX_SIZE,
        height: MARK_BOX_SIZE,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {status === 'done' ? (
        <Box
          sx={{
            width: DONE_MARK_SIZE,
            height: DONE_MARK_SIZE,
            borderRadius: '50%',
            bgcolor: 'success.bg',
            color: 'success.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Check sx={{ fontSize: 9 }} />
        </Box>
      ) : status === 'active' ? (
        <Spinner size={ACTIVE_SPINNER_SIZE} thickness={2} />
      ) : (
        <Box
          sx={{
            width: PENDING_DOT_SIZE,
            height: PENDING_DOT_SIZE,
            borderRadius: '50%',
            bgcolor: 'border.strong',
          }}
        />
      )}
    </Box>
  );
}

function useNow(): number {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), COUNTDOWN_TICK_MS);
    return () => clearInterval(interval);
  }, []);
  return now;
}

function timeLeft(seconds: number): string {
  if (seconds <= 0) return 'Finishing up';
  if (seconds < 60) return 'Less than a minute left';
  const minutes = Math.ceil(seconds / 60);
  return `About ${minutes} min left`;
}

// Counts the estimate down from when the poll arrived (not the server's clock), and never
// lets the bar move back when a stage overruns its budget; only the time text recovers.
function EstimatedProgress({ progress }: { progress: RunProgress }): ReactNode {
  const now = useNow();
  const [received, setReceived] = useState({ updatedAt: progress.updatedAt, at: Date.now() });
  const isNewEstimate = received.updatedAt !== progress.updatedAt;
  if (isNewEstimate) {
    setReceived({ updatedAt: progress.updatedAt, at: Date.now() });
  }
  const shownPercent = useRef(0);
  const elapsed = isNewEstimate ? 0 : Math.max(0, (now - received.at) / 1000);
  const remaining = Math.max(0, progress.etaSeconds - elapsed);
  const total = Math.max(progress.totalSeconds, 1);
  shownPercent.current = Math.max(
    shownPercent.current,
    ((total - Math.min(remaining, total)) / total) * 100,
  );
  return (
    <Stack sx={{ gap: 1.5 }}>
      <LinearProgress
        variant="determinate"
        value={shownPercent.current}
        aria-label={progress.label}
        sx={{ height: 6, borderRadius: 3 }}
      />
      <Stack direction="row" sx={{ gap: 2, justifyContent: 'space-between' }}>
        <Typography sx={{ color: 'text.primary' }}>{progress.label}</Typography>
        <Typography sx={{ color: 'text.secondary', flexShrink: 0 }}>
          {timeLeft(remaining)}
        </Typography>
      </Stack>
    </Stack>
  );
}

export function StepRunProgress({
  runningTitle,
  runningSteps,
  progress,
  onRunInBackground,
  backgroundHref,
}: StepRunProgressProps): ReactNode {
  const [phase, setPhase] = useState(0);
  const stepCount = progress == null ? runningSteps.length : 0;

  useEffect(() => {
    if (stepCount <= 1) {
      return;
    }
    const interval = setInterval(() => {
      setPhase((current) => Math.min(current + 1, stepCount - 1));
    }, STEP_ADVANCE_MS);
    return () => clearInterval(interval);
  }, [stepCount]);

  return (
    <Stack data-testid="step-run-progress" sx={{ gap: 3 }}>
      <Stack
        role="status"
        aria-live="polite"
        sx={{
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: `${radius.xl}px`,
          p: 4,
        }}
      >
        <Stack
          direction="row"
          sx={{ gap: 2, alignItems: 'center', mb: stepCount > 0 || progress != null ? 3 : 0 }}
        >
          <Spinner size={HEADER_SPINNER_SIZE} thickness={2.5} label={runningTitle} />
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'text.primary' }}>
            {runningTitle}
          </Typography>
        </Stack>
        {progress == null ? null : <EstimatedProgress progress={progress} />}
        {stepCount === 0 ? null : (
          <Stack sx={{ gap: 2 }}>
            {runningSteps.map((step, index) => {
              const status: StepStatus =
                index < phase ? 'done' : index === phase ? 'active' : 'pending';
              return (
                <Stack
                  key={step}
                  direction="row"
                  data-state={status}
                  sx={{ gap: 2, alignItems: 'center' }}
                >
                  <StepMark status={status} />
                  <Typography
                    sx={{
                      color: status === 'pending' ? 'text.secondary' : 'text.primary',
                      fontWeight: status === 'active' ? 600 : 400,
                    }}
                  >
                    {step}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
        )}
      </Stack>
      {backgroundHref !== undefined ? (
        <Button
          component="a"
          href={backgroundHref}
          variant="gradient"
          sx={{ alignSelf: 'flex-end' }}
        >
          Run in background
        </Button>
      ) : onRunInBackground === undefined ? null : (
        <Button variant="gradient" onClick={onRunInBackground} sx={{ alignSelf: 'flex-end' }}>
          Run in background
        </Button>
      )}
    </Stack>
  );
}
